import { memo, useMemo } from 'react'
import { useField, useFormState } from 'react-final-form'
import { FormItem } from '@cargomart/ui'
import { FieldRadio } from 'forms/FieldRadio'
import { required } from 'forms/validators'
import { checkFieldError, getFieldErrorMessage } from 'helpers/form'
import { OrderCreatePayer, OrderItemClient } from 'schema'

import { OrderClientBlock } from '../components'
import { useOrderCreateFormData } from '../pages/OrderCreateRoute/OrderCreateProvider'
import { TOrderCompany, TOrderContact } from '../types'
import { RadioSwitchValue, TOrderCreateFormData, TOrderCreatePayer } from '../types/orderCreateTypes'

interface IProps {
  customerTypeValue: OrderItemClient.type
  name: string
  className?: string
}

export const FieldOrderCreatePayer = memo<IProps>(function FieldOrderCreatePayer({
  className,
  name,
  customerTypeValue
}) {
  const { selectedCustomer, getCustomerContact, getPartnerContact } = useOrderCreateFormData()
  const formState = useFormState<TOrderCreateFormData>()
  const { departurePartner, destinationPartner, payer } = formState.values

  const customerContact = getCustomerContact()
  const departurePartnerContact = departurePartner && getPartnerContact(departurePartner)
  const destinationPartnerContact = destinationPartner && getPartnerContact(destinationPartner)

  const { meta } = useField<TOrderCreatePayer>(`${name}.role`)

  const hasSelectedLegalCustomer = !selectedCustomer?.id && customerTypeValue === OrderItemClient.type.LEGAL
  const isCustomerError = payer?.role === OrderCreatePayer.Role.CUSTOMER && hasSelectedLegalCustomer
  const isDepartureError =
    payer?.role === OrderCreatePayer.Role.DEPARTURE_PARTNER &&
    (departurePartner?.isUser === undefined ||
      (departurePartner?.isUser === RadioSwitchValue.yes && hasSelectedLegalCustomer))
  const isDestinationError =
    payer?.role === OrderCreatePayer.Role.DESTINATION_PARTNER &&
    (destinationPartner?.isUser === undefined ||
      (destinationPartner?.isUser === RadioSwitchValue.yes && hasSelectedLegalCustomer))

  const error = useMemo(() => getFieldErrorMessage(checkFieldError(meta)), [meta])

  return (
    <div className={className}>
      <h2 className="mb-3 mt-4 text-headline-sm">Плательщик</h2>
      <div className="flex flex-col">
        <FormItem className="inline-flex flex-col gap-y-1">
          <FieldRadio name={`${name}.role`} validate={required} value={OrderCreatePayer.Role.CUSTOMER}>
            Заказчик
          </FieldRadio>
          {isCustomerError && <span className="text-body-sm text-negative">Выберите заказчика</span>}
          <FieldRadio name={`${name}.role`} validate={required} value={OrderCreatePayer.Role.DEPARTURE_PARTNER}>
            Отправитель
          </FieldRadio>
          {isDepartureError && <span className="text-body-sm text-negative">Выберите отправителя</span>}
          <FieldRadio name={`${name}.role`} validate={required} value={OrderCreatePayer.Role.DESTINATION_PARTNER}>
            Получатель
          </FieldRadio>
          {isDestinationError && <span className="text-body-sm text-negative">Выберите получателя</span>}
        </FormItem>
        <span className={error ? 'text-body-sm text-negative' : ''}>{error}</span>
      </div>
      {payer?.role === OrderCreatePayer.Role.CUSTOMER && !isCustomerError && (
        <OrderClientBlock
          isWithoutContact
          orderClientCompany={customerContact.company}
          orderClientContact={customerContact.contact}
          orderClientType={customerTypeValue}
        />
      )}
      {payer?.role === OrderCreatePayer.Role.DEPARTURE_PARTNER && !isDepartureError && (
        <OrderClientBlock
          isWithoutContact
          orderClientCompany={departurePartnerContact?.company as unknown as TOrderCompany}
          orderClientContact={departurePartnerContact?.contact as unknown as TOrderContact}
          orderClientType={departurePartnerContact?.type}
        />
      )}
      {payer?.role === OrderCreatePayer.Role.DESTINATION_PARTNER && !isDestinationError && (
        <OrderClientBlock
          isWithoutContact
          orderClientCompany={destinationPartnerContact?.company as unknown as TOrderCompany}
          orderClientContact={destinationPartnerContact?.contact as unknown as TOrderContact}
          orderClientType={destinationPartnerContact?.type}
        />
      )}
    </div>
  )
})
